import {
  AfterViewInit,
  Component,
  EventEmitter,
  Input,
  OnChanges,
  OnDestroy,
  Output,
  SimpleChanges,
  ViewChild,
} from '@angular/core';
import { ApexChart, ChartComponent } from 'ng-apexcharts';
import { ChartOptions2 } from 'services/models/charts/ChartOptions';
import { FormMetricsCategoryChartData } from 'services/models/form-builder/form-metrics/chart-data/category-chart-data.model';
import { FormMetricsCategoryClickEvent } from 'services/models/form-builder/form-metrics/form-metrics-drilldown.model';
import { FormMetricsRespondentAnswerItemModel } from 'services/models/form-builder/form-metrics/form-metrics-drilldown.model';
import { FormMetricsItemModel } from 'services/models/form-builder/form-metrics/form-metrics-item.model';
import { FormMetricsChartMapperService } from '../../services/form-metrics-chart-mapper.service';
import { FormMetricsLegendItem } from '../../utils/form-metrics-legend.util';
import {
  applyHighlightColors,
  buildCategoryClickEvent,
  buildHighlightStroke,
  findHighlightForItem,
  FormMetricsSliceMeta,
  resolveHighlightedIndexes,
} from '../../utils/form-metrics-drilldown.util';

@Component({
  selector: 'app-form-metrics-pie-widget',
  templateUrl: './form-metrics-pie-widget.component.html',
  styleUrls: ['./form-metrics-pie-widget.component.scss'],
})
export class FormMetricsPieWidgetComponent implements OnChanges, AfterViewInit, OnDestroy {
  @Input() item!: FormMetricsItemModel;
  @Input() categoryClickEnabled = false;
  @Input() highlightAnswers: FormMetricsRespondentAnswerItemModel[] = [];
  @Output() categoryClick = new EventEmitter<FormMetricsCategoryClickEvent>();

  @ViewChild('chartRef') chartRef: ChartComponent | undefined;

  chartOptions: Partial<ChartOptions2> | null = null;
  legendItems: FormMetricsLegendItem[] = [];

  private highlightedIndexes: number[] = [];
  private chartReady = false;
  private expandTimeout: any;

  constructor(
    private chartMapper: FormMetricsChartMapperService
  ) {}

  ngAfterViewInit(): void {
    this.chartReady = true;
    this.expandHighlightedSlices();
  }

  ngOnChanges(_changes: SimpleChanges): void {
    this.rebuild();
    if (this.chartReady) {
      clearTimeout(this.expandTimeout);
      this.expandTimeout = setTimeout(() => this.expandHighlightedSlices(), 100);
    }
  }

  ngOnDestroy(): void {
    clearTimeout(this.expandTimeout);
  }

  private rebuild(): void {
    if (!this.item || this.item.totalAnswers <= 0) {
      this.chartOptions = null;
      this.legendItems = [];
      this.highlightedIndexes = [];
      return;
    }

    const mapped = this.chartMapper.mapPie(this.item);
    const data = this.item.chartData as FormMetricsCategoryChartData;
    const slices: FormMetricsSliceMeta[] = (data?.categories || []).map(c => ({
      optionId: c.optionId,
      label: c.label,
      value: null,
    }));
    const highlight = findHighlightForItem(this.highlightAnswers, this.item.formItemId);
    this.highlightedIndexes = resolveHighlightedIndexes(slices, highlight);
    const baseColors = (mapped.chartOptions.colors as string[]) || [];

    const chart: ApexChart = {
      ...(mapped.chartOptions.chart as ApexChart),
    };
    if (this.categoryClickEnabled) {
      chart.events = {
        dataPointSelection: (_event, _ctx, config) => {
          const index = config?.dataPointIndex ?? config?.seriesIndex;
          const slice = slices[index];
          if (!slice) {
            return;
          }
          this.categoryClick.emit(
            buildCategoryClickEvent(this.item.formItemId, this.item.chartType, slice)
          );
        },
      };
    }

    this.chartOptions = {
      ...mapped.chartOptions,
      colors: applyHighlightColors(baseColors, this.highlightedIndexes),
      chart,
    };

    const stroke = buildHighlightStroke(baseColors, this.highlightedIndexes);
    if (stroke) {
      this.chartOptions.stroke = { width: stroke.width, colors: stroke.colors };
    }

    this.legendItems = mapped.legendItems;
  }

  private expandHighlightedSlices(): void {
    if (!this.chartRef || !this.highlightedIndexes.length) {
      return;
    }

    for (const index of this.highlightedIndexes) {
      this.chartRef.toggleDataPointSelection(0, index);
    }
  }
}
